import { html, css, LitElement } from 'lit-element';
import { sendPasswordResetEmail } from 'firebase/auth';

import '@material/mwc-dialog';
import '@material/mwc-button';
import '@material/mwc-textfield';

import { auth } from './intrivity_app.js';
import {
    emitEvent, INTRIVITY_LOGIN, INTRIVITY_LOGIN_ERROR, INTRIVITY_GOOGLE_LOGGED_IN
} from './utility.js';
import { a11yStyles } from './a11y.js';
import { t, Localized } from './i18n.js';

// Offers Google, Microsoft, and email/password sign-in (sign-in or sign-up)
// behind one dialog. All sign-in attempts are routed through the same
// INTRIVITY_LOGIN event intrivity_app.js already listens for — this
// component never talks to Firebase Auth directly except for the
// password-reset email, which doesn't sign anyone in.
export class IntrivityLoginDialog extends Localized(LitElement) {

    static get styles() {
        return [a11yStyles, css`
            mwc-dialog {
                --mdc-dialog-min-width: min(90vw, 420px);
            }
            .oauth-buttons {
                display: flex;
                flex-direction: column;
                gap: 10px;
                margin-bottom: 16px;
            }
            .oauth-buttons mwc-button {
                width: 100%;
            }
            .divider {
                display: flex;
                align-items: center;
                text-align: center;
                /* #999 was 2.8:1 on white */
                color: #6b6b6b;
                font-size: 0.8em;
                margin: 16px 0;
            }
            .divider::before, .divider::after {
                content: '';
                flex: 1;
                border-bottom: 1px solid #ddd;
            }
            .divider::before { margin-right: 0.5em; }
            .divider::after { margin-left: 0.5em; }
            mwc-textfield {
                width: 100%;
                margin-bottom: 12px;
            }
            .submit-btn {
                width: 100%;
            }
            .error {
                color: #b00020;
                font-size: 0.85em;
                margin-bottom: 12px;
            }
            .switch-mode, .forgot {
                font-size: 0.85em;
                text-align: center;
                margin-top: 8px;
                cursor: pointer;
                /* Darkened from rgb(87,130,133), which was 4.25:1 on white at
                   0.85em — under the AA threshold for text this size. */
                color: rgb(60, 96, 99);
                text-decoration: underline;
                display: block;
                width: 100%;
            }
            .status {
                color: #1a6b1a;
                font-size: 0.85em;
                margin-bottom: 12px;
            }
        `];
    }

    static get properties() {
        return {
            open: { type: Boolean },
            _mode: { type: String },       // 'signin' | 'signup'
            _email: { type: String },
            _password: { type: String },
            _confirmPassword: { type: String },
            _firstName: { type: String },
            _lastName: { type: String },
            _error: { type: String },
            _status: { type: String },
            _busy: { type: Boolean },
        };
    }

    constructor() {
        super();
        this.open = false;
        this._mode = 'signin';
        this._email = '';
        this._password = '';
        this._confirmPassword = '';
        this._firstName = '';
        this._lastName = '';
        this._error = '';
        this._status = '';
        this._busy = false;

        this._onLoginError = this._onLoginError.bind(this);
        this._onLoggedIn = this._onLoggedIn.bind(this);
    }

    connectedCallback() {
        super.connectedCallback();
        document.addEventListener(INTRIVITY_LOGIN_ERROR, this._onLoginError);
        document.addEventListener(INTRIVITY_GOOGLE_LOGGED_IN, this._onLoggedIn);
    }

    disconnectedCallback() {
        document.removeEventListener(INTRIVITY_LOGIN_ERROR, this._onLoginError);
        document.removeEventListener(INTRIVITY_GOOGLE_LOGGED_IN, this._onLoggedIn);
        super.disconnectedCallback();
    }

    _onLoginError(e) {
        this._busy = false;
        this._error = (e.detail && e.detail.message) || t('common.genericError');
    }

    _onLoggedIn() {
        this._busy = false;
        this._error = '';
        this._close();
    }

    _close() {
        this.open = false;
        this._email = '';
        this._password = '';
        this._confirmPassword = '';
        this._firstName = '';
        this._lastName = '';
        this._error = '';
        this._status = '';
        this._mode = 'signin';
        this.dispatchEvent(new CustomEvent('dialog-closed', {bubbles: true, composed: true}));
    }

    _toggleMode() {
        this._mode = this._mode === 'signin' ? 'signup' : 'signin';
        this._error = '';
        this._status = '';
    }

    _loginWithGoogle() {
        this._error = '';
        this._busy = true;
        emitEvent(INTRIVITY_LOGIN, {provider: 'google'}, this);
    }

    _loginWithMicrosoft() {
        this._error = '';
        this._busy = true;
        emitEvent(INTRIVITY_LOGIN, {provider: 'microsoft'}, this);
    }

    _submitEmailForm() {
        this._error = '';

        if (!this._email || !this._password) {
            this._error = t('login.missingFields');
            return;
        }
        if (this._mode === 'signup' && (!this._firstName.trim() || !this._lastName.trim())) {
            this._error = t('login.missingName');
            return;
        }
        if (this._mode === 'signup' && this._password !== this._confirmPassword) {
            this._error = t('login.passwordMismatch');
            return;
        }

        this._busy = true;
        emitEvent(INTRIVITY_LOGIN, {
            provider: 'email',
            mode: this._mode,
            email: this._email,
            password: this._password,
            firstName: this._firstName.trim(),
            lastName: this._lastName.trim(),
        }, this);
    }

    _handleKeydown(e) {
        if (e.key === 'Enter') {
            e.preventDefault();
            this._submitEmailForm();
        }
    }

    async _forgotPassword() {
        if (!this._email) {
            this._error = t('login.forgotNeedsEmail');
            return;
        }
        try {
            await sendPasswordResetEmail(auth, this._email);
            this._error = '';
            this._status = t('login.resetSent', { email: this._email });
        } catch (err) {
            this._error = err.message || t('login.resetFailed');
        }
    }

    render() {
        return html`
            <mwc-dialog ?open=${this.open} heading="${this._mode === 'signup' ? t('login.createAccount') : t('login.logIn')}" @closed=${() => this._close()}>
                <div class="error" role="alert">${this._error}</div>
                <div class="status" role="status">${this._status}</div>

                <div class="oauth-buttons">
                    <mwc-button raised ?disabled=${this._busy} @click=${() => this._loginWithGoogle()} label=${t('login.google')}></mwc-button>
                    <mwc-button raised ?disabled=${this._busy} @click=${() => this._loginWithMicrosoft()} label=${t('login.microsoft')}></mwc-button>
                </div>

                <div class="divider">${t('login.or')}</div>

                <div>
                    ${this._mode === 'signup' ? html`
                        <mwc-textfield label=${t('common.firstName')} required autocomplete="given-name" .value=${this._firstName} @input=${(e) => this._firstName = e.target.value} @keydown=${(e) => this._handleKeydown(e)}></mwc-textfield>
                        <mwc-textfield label=${t('common.lastName')} required autocomplete="family-name" .value=${this._lastName} @input=${(e) => this._lastName = e.target.value} @keydown=${(e) => this._handleKeydown(e)}></mwc-textfield>
                    `: html``}
                    <mwc-textfield label=${t('login.email')} type="email" required autocomplete="email" .value=${this._email} @input=${(e) => this._email = e.target.value} @keydown=${(e) => this._handleKeydown(e)}></mwc-textfield>
                    <mwc-textfield label=${t('login.password')} type="password" required .value=${this._password} @input=${(e) => this._password = e.target.value} @keydown=${(e) => this._handleKeydown(e)}></mwc-textfield>
                    ${this._mode === 'signup' ? html`
                        <mwc-textfield label=${t('login.confirmPassword')} type="password" .value=${this._confirmPassword} @input=${(e) => this._confirmPassword = e.target.value} @keydown=${(e) => this._handleKeydown(e)}></mwc-textfield>
                    `: html``}
                    <mwc-button class="submit-btn" raised ?disabled=${this._busy} @click=${() => this._submitEmailForm()} label=${this._mode === 'signup' ? t('login.createAccount') : t('login.logIn')}></mwc-button>
                </div>

                ${this._mode === 'signin' ? html`
                    <button type="button" class="unstyled-button forgot" @click=${() => this._forgotPassword()}>${t('login.forgotPassword')}</button>
                `: html``}

                <button type="button" class="unstyled-button switch-mode" @click=${() => this._toggleMode()}>
                    ${this._mode === 'signup' ? t('login.haveAccount') : t('login.needAccount')}
                </button>

                <mwc-button slot="secondaryAction" dialogAction="close" label=${t('common.cancel')}></mwc-button>
            </mwc-dialog>
        `;
    }
}
if (!customElements.get('intrivity-login-dialog')) customElements.define('intrivity-login-dialog', IntrivityLoginDialog);
